import React, { forwardRef, useId } from "react"

import * as Headless from "@headlessui/react"
import clsx from "clsx"

import { CONTROL_HEIGHT_CLASS } from "./control"
import { Description, ErrorMessage, Field, Label } from "./fieldset"
import type { InputProps } from "./input"
import { Input, InputGroup } from "./input"

type IconComponent = React.ComponentType<{
  className?: string
  "data-slot"?: string
}>

type ForwardedInputProps = Omit<
  InputProps,
  | `aria-describedby`
  | `aria-errormessage`
  | `aria-invalid`
  | `aria-label`
  | `aria-labelledby`
  | `id`
>

export type TextFieldProps = ForwardedInputProps & {
  label?: React.ReactNode
  labelSrOnly?: boolean
  description?: React.ReactNode
  error?: React.ReactNode
  leadingIcon?: IconComponent
  trailingIcon?: IconComponent
  leadingAddon?: React.ReactNode
  trailingAddon?: React.ReactNode
}

const IS_DEV_BUILD = process.env.NODE_ENV !== `production`

const ADDON_TEXT_CLASSES = clsx(
  `shrink-0 select-none text-base/6 text-muted sm:text-sm/6`,
)

export const TextField = forwardRef(function TextField(
  {
    label,
    labelSrOnly = false,
    description,
    error,
    leadingIcon: LeadingIcon,
    trailingIcon: TrailingIcon,
    leadingAddon,
    trailingAddon,
    className,
    disabled,
    ...inputProps
  }: TextFieldProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const errorId = useId()
  const errorMessageId = error ? errorId : undefined

  if (IS_DEV_BUILD && !label && !labelSrOnly) {
    console.error(
      `<TextField> requires a 'label' prop, or 'labelSrOnly' must be true to acknowledge the field is intentionally unlabelled.`,
    )
  }

  const hasAddon = leadingAddon !== undefined || trailingAddon !== undefined
  const hasIcon = LeadingIcon !== undefined || TrailingIcon !== undefined

  let control: React.ReactNode

  if (hasAddon) {
    control = (
      <span
        data-slot="control"
        className={clsx(
          `relative block w-full`,
          `before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-background before:shadow-sm`,
          `after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-focus-ring`,
          `has-data-disabled:opacity-50 has-data-disabled:before:bg-surface has-data-disabled:before:shadow-none`,
        )}
      >
        <div
          className={clsx(
            CONTROL_HEIGHT_CLASS,
            `relative flex items-center gap-1 rounded-lg border border-control-border`,
            `has-[[aria-invalid=true]]:border-danger`,
            `has-[input:disabled]:border-control-border`,
          )}
        >
          {leadingAddon !== undefined ? (
            <span
              data-slot="addon"
              className={clsx(ADDON_TEXT_CLASSES, `pl-3`)}
            >
              {leadingAddon}
            </span>
          ) : null}
          <Headless.Input
            {...inputProps}
            ref={ref}
            invalid={Boolean(error)}
            disabled={disabled}
            aria-errormessage={errorMessageId}
            className={clsx(
              `block h-full min-w-0 grow appearance-none bg-transparent`,
              `pl-3 pr-3`,
              `text-base/6 text-foreground placeholder:text-muted sm:text-sm/6`,
              `focus:outline-hidden`,
            )}
          />
          {trailingAddon !== undefined ? (
            <span
              data-slot="addon"
              className={clsx(ADDON_TEXT_CLASSES, `pr-3`)}
            >
              {trailingAddon}
            </span>
          ) : null}
        </div>
      </span>
    )
  } else {
    const inputElement = (
      <Input
        {...inputProps}
        ref={ref}
        invalid={Boolean(error)}
        aria-errormessage={errorMessageId}
      />
    )
    control = hasIcon ? (
      <InputGroup>
        {LeadingIcon ? <LeadingIcon data-slot="icon" /> : null}
        {inputElement}
        {TrailingIcon ? <TrailingIcon data-slot="icon" /> : null}
      </InputGroup>
    ) : (
      inputElement
    )
  }

  return (
    <Field className={className} disabled={disabled}>
      {label ? (
        <Label className={labelSrOnly ? `sr-only` : undefined}>{label}</Label>
      ) : null}
      {description ? <Description>{description}</Description> : null}
      {control}
      {error ? <ErrorMessage id={errorMessageId}>{error}</ErrorMessage> : null}
    </Field>
  )
})
